import React, { useState, useEffect } from "react";
import "./iconFilter.css";
import { LuFilter, LuX } from "react-icons/lu";

const ACTIVE_ITEM_KEY = "__activeItemName";

export const getIconFilterKey = (filterOption) =>
  filterOption?.name || "iconFilter";

const getDefaultSelectedValues = (item) =>
  item?.subfilter
    ?.flatMap((subfilter) => subfilter.fields || [])
    ?.filter((field) => field.type === "checkbox")
    ?.flatMap((field) => field.values || []) || [];

const getInitialActiveItem = (filterOption, activeItemName) =>
  filterOption?.options?.find((opt) => opt.imageName === activeItemName) ||
  filterOption?.options?.find(
    (opt) => opt.isSelected === "true" || opt.isSelected === true
  ) ||
  filterOption?.options?.[0] ||
  null;

const getPopupSubfilter = (item) =>
  item?.subfilter?.find((subfilter) => subfilter.type === "popup") || null;

const createSelectedValuesForItem = (item, existingValues = {}, selectAll = false) => {
  const existingItemValues = existingValues[item?.imageName];

  return {
    ...existingValues,
    [ACTIVE_ITEM_KEY]: item?.imageName,
    [item?.imageName]:
      selectAll || existingItemValues === undefined
        ? getDefaultSelectedValues(item)
        : existingItemValues,
  };
};

const IconFilter = ({
  filterOption,
  onFilterChange,
  selectedFilters
}) => {
  const filterKey = getIconFilterKey(filterOption);
  const values = selectedFilters[filterKey] || {};
  const selectedActiveItemName = values[ACTIVE_ITEM_KEY];
  const initialActiveItem = getInitialActiveItem(
    filterOption,
    selectedActiveItemName
  );

  const [selectedCategory, setSelectedCategory] = useState(initialActiveItem);
  const [pendingCategory, setPendingCategory] = useState(null);
  const [draftValues, setDraftValues] = useState(() =>
    initialActiveItem
      ? createSelectedValuesForItem(initialActiveItem, values)
      : values
  );
  const [activeSubfilter, setActiveSubfilter] = useState(null);

  const popupCategory = pendingCategory || selectedCategory;

  useEffect(() => {
    const selected = getInitialActiveItem(filterOption, selectedActiveItemName);

    if (selected) {
      setSelectedCategory(selected);
      setPendingCategory(null);
      setActiveSubfilter(null);
      setDraftValues(createSelectedValuesForItem(selected, {}, true));
    }
  }, [filterOption]);

  const closePopup = () => {
    setPendingCategory(null);
    setActiveSubfilter(null);
    if (selectedCategory) {
      setDraftValues(createSelectedValuesForItem(selectedCategory, values));
    }
  };

  const openCategoryFilter = (item) => {
    const popupSubfilter = getPopupSubfilter(item);
    if (!popupSubfilter) {
      return;
    }

    setPendingCategory(item);
    setDraftValues(createSelectedValuesForItem(item, values));
    setActiveSubfilter(popupSubfilter);
  };

  const toggleCheckbox = (groupId, value) => {
    const current = draftValues[groupId] || [];

    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];

    setDraftValues({
      ...draftValues,
      [ACTIVE_ITEM_KEY]: groupId,
      [groupId]: updated
    });
  };

  const handleRadioChange = (groupId, value) => {
    setDraftValues({
      ...draftValues,
      [ACTIVE_ITEM_KEY]: groupId,
      [groupId]: value
    });
  };

  const applyFilters = () => {
    const categoryToCommit = pendingCategory || selectedCategory;

    if (categoryToCommit) {
      setSelectedCategory(categoryToCommit);
    }

    onFilterChange(filterKey, draftValues);
    setPendingCategory(null);
    setActiveSubfilter(null);
  };

  return (
    <>
      <div className="iconFilterSection">
        <div className="iconFilter">
          {filterOption?.options?.map((item) => {
            const isSelected = selectedCategory?.imageName === item.imageName;
            const hasPopup = Boolean(getPopupSubfilter(item));

            return (
              <button
                type="button"
                key={item.imageName}
                className={`iconCard ${isSelected ? "selected" : ""}`}
                aria-label={`Open ${item.imageName} filters`}
                aria-pressed={isSelected}
                disabled={!hasPopup}
                onClick={() => openCategoryFilter(item)}
              >
                {hasPopup && (
                  <span className="iconCardFilterBtn" aria-hidden="true">
                    <LuFilter />
                  </span>
                )}
                <div className="iconCircle">
                  <img src={item.imageUrl} alt={item.imageName} />
                </div>
                <span className="iconCardLabel">{item.imageName}</span>
              </button>
            );
          })}
        </div>
      </div>

      {activeSubfilter && popupCategory && (
        <div className="iconPopupOverlay" onClick={closePopup}>
          <div
            className="iconPopup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="iconPopupTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="iconPopupHeader">
              <h3 id="iconPopupTitle">{popupCategory.imageName}</h3>
              <button
                type="button"
                className="iconPopupClose"
                aria-label="Close filter popup"
                onClick={closePopup}
              >
                <LuX />
              </button>
            </div>

            {activeSubfilter.fields?.map((field, index) => (
              <div key={index} className="filterField">
                {field.type === "checkbox" &&
                  field.values.map((value) => {
                    const checked = (
                      draftValues[popupCategory.imageName] || []
                    ).includes(value);

                    return (
                      <label
                        key={value}
                        className={`filterChoice ${checked ? "selected" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            toggleCheckbox(popupCategory.imageName, value)
                          }
                        />
                        <span className="filterChoiceControl" aria-hidden="true" />
                        <span className="filterChoiceText">{value}</span>
                      </label>
                    );
                  })}

                {field.type === "radio" &&
                  field.values.map((value) => {
                    const checked =
                      draftValues[popupCategory.imageName] === value;

                    return (
                      <label
                        key={value}
                        className={`filterChoice ${checked ? "selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name={popupCategory.imageName}
                          checked={checked}
                          onChange={() =>
                            handleRadioChange(popupCategory.imageName, value)
                          }
                        />
                        <span className="filterChoiceControl" aria-hidden="true" />
                        <span className="filterChoiceText">{value}</span>
                      </label>
                    );
                  })}
              </div>
            ))}

            <div className="iconPopupFooter">
              <button
                type="button"
                className="iconPopupApply"
                onClick={applyFilters}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IconFilter;
